import React, { createContext, useState, useEffect, useContext } from 'react';
import { useMsal } from "@azure/msal-react";
import {InteractionRequiredAuthError, InteractionStatus} from "@azure/msal-browser";
import {graphConfig, backendTokenRequest} from "../Config/EntraAuthConfig";
import ApiService from "../Services/ApiService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [account, setAccount] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [backendToken, setBackendToken] = useState(null);
    const [permissions, setPermissions] = useState({
        isAdmin: false,
        isReader: false,
        isWriter: false,
    });

    const GROUPS = {
        ADMIN: process.env.REACT_APP_ENTRA_GROUP_ADMINS,
        READER: process.env.REACT_APP_ENTRA_GROUP_READERS,
        WRITER: process.env.REACT_APP_ENTRA_GROUP_WRITERS,
    };

    const fetchAllUserGroups = async (token) => {
        let allGroups = [];
        let nextLink = graphConfig.graphMeMemberOfEndpoint;

        try {
            while (nextLink) {
                const response = await fetch(nextLink, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) throw new Error("Error loading Entra groups");

                const data = await response.json();
                allGroups = [...allGroups, ...data.value];

                nextLink = data["@odata.nextLink"] || null;
            }
        } catch (error) {
            console.error("Error loading the Entra groups", error);
        }

        return allGroups.map((group) => group.displayName);
    };

    const getTokenAndGroups = async () => {
        if (accounts.length > 0 && inProgress === InteractionStatus.None) {
            try {
                setAccount(accounts[0]);
                const response = await instance.acquireTokenSilent({
                    scopes: ["User.Read", "GroupMember.Read.All"],
                    account: accounts[0],
                });
                setAccessToken(response.accessToken);

                const userGroups = await fetchAllUserGroups(response.accessToken);

                const newPermissions = {
                    isAdmin: userGroups.includes(GROUPS.ADMIN),
                    isWriter: userGroups.includes(GROUPS.WRITER),
                    isReader: userGroups.includes(GROUPS.READER),
                };
                setPermissions(newPermissions);

                fetchBackendToken();

            } catch (error) {
                console.error("Error loading the Entra token and groups", error);
                if (error instanceof InteractionRequiredAuthError) {
                    try {
                        await instance.loginRedirect({
                            scopes: ["User.Read", "GroupMember.Read.All"]
                        });
                    } catch (authError) {
                        console.error("Authentication failed", authError);
                    }
                }
            }
        }
    };

    const fetchBackendToken = async () => {
        try {
            const response = await instance.acquireTokenSilent({
                ...backendTokenRequest,
                account: accounts[0],
            });
            setBackendToken(response.accessToken);
            ApiService.setToken(response.accessToken);
        } catch (error) {
            console.error("Error loading the backend token", error);
            if (error instanceof InteractionRequiredAuthError) {
                try {
                    await instance.acquireTokenRedirect({
                        ...backendTokenRequest,
                        account: accounts[0],
                    });
                } catch (authError) {
                    console.error("Backend token acquisition failed", authError);
                }
            }
        }
    };

    useEffect(() => {
        if (accessToken) {
            const expirationTime = 50 * 60 * 1000;
            const refreshInterval = setInterval(getTokenAndGroups, expirationTime);
            return () => clearInterval(refreshInterval);
        }
    }, [accessToken]);

    useEffect(() => {
        if (accounts.length === 0 && inProgress === InteractionStatus.None) {
            instance.loginRedirect({
                scopes: ["User.Read", "GroupMember.Read.All"],
            });
        } else {
            getTokenAndGroups();
        }
    }, [accounts, inProgress, instance]);

    return (
        <AuthContext.Provider value={{ accessToken, backendToken, permissions, account }}>
            {accessToken ? children :
                <div className="loading-container w-100">
                    <h1><span className="animated-dots"></span></h1>
                </div>
            }
        </AuthContext.Provider>
    );
};


export const useAuth = () => useContext(AuthContext);
